import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { rgb } from 'd3-color';
import { interpolateRgb } from 'd3-interpolate';
import { scaleLinear } from 'd3-scale';
import { arc } from 'd3-shape';
import { ObservableInput } from 'observable-input';
import {
  animationFrameScheduler,
  combineLatest,
  Observable,
  Subject,
  timer,
} from 'rxjs';
import {
  delay,
  filter,
  map,
  scan,
  shareReplay,
  switchMap,
  takeWhile,
} from 'rxjs/operators';
import { Size } from '../core/size.service';

export type GaugeDatum = {
  min: number;
  max: number;
  value: number;
};
let unique = 0;

const interval = 1000 / 30;
const animateTimer = timer(0, interval, animationFrameScheduler).pipe(
  map(i => i * interval),
);

function easeIn(time: number, begin: number, end: number, duration: number) {
  return (
    (end - begin) * Math.pow(Math.min(duration, time) / duration, 3) + begin
  );
}

function ease(
  begin: number,
  end: number,
  duration: number,
  easeFn: (
    time: number,
    begin: number,
    end: number,
    duration: number,
  ) => number,
) {
  return animateTimer.pipe(
    takeWhile(t => t <= duration),
    map(time => easeFn(time, begin, end, duration)),
  );
}

@Component({
  selector: 'gauge',
  templateUrl: './gauge.component.html',
  styleUrls: ['./gauge.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GaugeComponent {
  constructor(readonly location: Location) {
    this.pageGradientId = `gaugeGradientId-${unique++}`;
    this.pageGradientId2 = `gaugeGradientId-${unique++}`;
  }

  @ObservableInput()
  @Input()
  data!: Observable<GaugeDatum>;

  easedValue = this.data.pipe(
    map(d => d.value),
    scan(
      ([_, to], value) =>
        to == null
          ? <[number, number]>[value, value]
          : <[number, number]>[to, value],
      <[number | null, number | null]>[null, null],
    ),
    filter(([from, to]) => from != to),
    delay(300),
    switchMap(([from, to]) => ease(from!, to!, 500, easeIn)),
    shareReplay(1),
  );

  angle = combineLatest([this.data, this.easedValue]).pipe(
    map(([data, value]) =>
      scaleLinear()
        .domain([data.min, data.max])
        .range([(-5 * Math.PI) / 6, (5 * Math.PI) / 6])
        .clamp(true)(value),
    ),
  );

  gradientScale = combineLatest([this.data, this.easedValue]).pipe(
    map(([data, value]) => {
      const midPoint = (data.max - data.min) / 2;
      return scaleLinear()
        .domain([data.min, midPoint])
        .range([0, 1])
        .clamp(true)(value);
    }),
  );
  gradientScale2 = combineLatest([this.data, this.easedValue]).pipe(
    map(([data, value]) => {
      const midPoint = (data.max - data.min) / 2;
      return scaleLinear()
        .domain([midPoint, data.max])
        .range([5, 1])
        .clamp(true)(value);
    }),
  );

  readonly svgSize = new Subject<Size>();

  readonly graphTransform = this.svgSize.pipe(
    map(size => `translate(${size.width / 2} ${size.height / 2})`),
  );

  readonly radii = this.svgSize.pipe(
    map(size => {
      const outer = (Math.min(size.width, size.height) * 0.9) / 2;
      const inner = outer - 4;
      return { inner, outer };
    }),
  );

  readonly greyArcPath = this.radii.pipe(
    map(({ inner, outer }) =>
      arc()({
        innerRadius: inner - 4,
        outerRadius: outer - 4,
        startAngle: (-5 * Math.PI) / 6,
        endAngle: (5 * Math.PI) / 6,
      }),
    ),
  );

  readonly arcPath = combineLatest([this.angle, this.radii]).pipe(
    map(([angle, { inner, outer }]) =>
      arc()({
        innerRadius: inner,
        outerRadius: outer,
        startAngle: (-5 * Math.PI) / 6,
        endAngle: 0,
      }),
    ),
  );

  readonly arcPath2 = combineLatest([this.angle, this.radii]).pipe(
    map(([angle, { inner, outer }]) =>
      arc()({
        innerRadius: inner,
        outerRadius: outer,
        startAngle: 0,
        endAngle: (5 * Math.PI) / 6,
      }),
    ),
  );

  readonly coverArcPath = combineLatest([this.angle, this.radii]).pipe(
    map(([angle, { inner, outer }]) =>
      arc()({
        innerRadius: inner,
        outerRadius: outer,
        startAngle: angle!,
        endAngle: (5 * Math.PI) / 6,
      }),
    ),
  );

  colorRange = scaleLinear()
    .domain([(-5 * Math.PI) / 6, 0, (5 * Math.PI) / 6])
    .range([<any>rgb('#FC7022'), <any>rgb('#FBDB6C'), <any>rgb('#43C285')])
    .interpolate(<any>interpolateRgb);

  readonly gaugePoint = combineLatest([this.angle, this.radii]).pipe(
    map(([angle, { inner, outer }]) => ({
      x: (outer - 2) * Math.cos(angle! - Math.PI / 2),
      y: (outer - 2) * Math.sin(angle! - Math.PI / 2),
    })),
  );

  readonly gaugeColor = this.angle.pipe(map(angle => this.colorRange(angle!)));

  pageGradientId: string;

  pageGradientUrl = () => `url(${this.location.path()}#${this.pageGradientId})`;
  pageGradientId2: string;

  pageGradientUrl2 = () =>
    `url(${this.location.path()}#${this.pageGradientId2})`;
}
